import {
  Link,
  useNavigate,
  useParams,
  useSearchParams
} from "react-router-dom";
import { useMemo } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";

import { T } from "@repo/transifex";
import { useGetMenus } from "~/api/menus/hooks/use-get-menus";
import { ErrorDisplay } from "~/components/collect/error-display";
import { Button } from "~/components";
import { LoadingSpinner } from "~/components/LoadingSpinner";
import { useCurrentTillLocalisation } from "~/api/tills/hooks/use-till-current/localisation";
import { priceFormatter } from "~/utils/price-formatter";
import { useUser } from "~/api/users/hooks/use-user";
import { AllergensIcons } from "~/components/allergens";
import { useCollectAllergyMatchers } from "~/hooks/use-collect-allergy-matcher";

import { useCollectDebtGate } from "../__app.collect.orders.create/use-collect-debt-gate";

import { VariantHeader } from "./variant-header";
import { VariantForm } from "./variant-form";
import { VariantsGridLayout } from "./variants-grid-layout";
import {
  buildPreOrderCandidate,
  previewPickedExtrasInclVat
} from "./build-pre-order-candidate";

export type VariantFormData = Record<string, number>;

export type ProductFormData = {
  variants: Record<string, VariantFormData | null> | null;
};

export const CollectChildOrderCreateProductPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const childId = searchParams.get("childId");
  const moduleId = searchParams.get("moduleId");
  const { productId } = useParams();

  const { locale, currency } = useCurrentTillLocalisation();
  const { data: user, status: userStatus } = useUser({ childId });
  const matchAllergiesFor = useCollectAllergyMatchers(childId);
  const { data } = useGetMenus({
    childId: childId ?? "unknown"
  });

  const { takeCandidate } = useCollectDebtGate({ childId });

  const { product, variants, discounts } = useMemo(() => {
    const menu = data?.menus.find((menu) => menu.moduleId === moduleId);
    const product = menu?.products.find((product) => product.id === productId);

    const variants = menu?.variants.filter(
      (variant) => product?.variantIds?.includes(variant.id)
    );

    return { product, variants, discounts: menu?.discounts ?? [] };
  }, [data?.menus, moduleId, productId]);

  const {
    control,
    handleSubmit,
    formState: { errors }
  } = useForm<ProductFormData>({
    defaultValues: {
      variants: null
    }
  });

  const selectedVariants = useWatch({ control, name: "variants" });

  if (!childId || !moduleId || !product || !variants?.length) {
    return (
      <ErrorDisplay>
        <ErrorDisplay.Title>
          <T _str="Something went wrong!" />
        </ErrorDisplay.Title>
        <ErrorDisplay.Description>
          <T _str="We couldn’t load the product" />
        </ErrorDisplay.Description>
        <Link to="..">
          <ErrorDisplay.Action variant="danger">
            <T _str="Go back" />
          </ErrorDisplay.Action>
        </Link>
      </ErrorDisplay>
    );
  }

  if (!user && userStatus === "pending") {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-interface-neutral-1">
        <LoadingSpinner />
      </div>
    );
  }

  const toCandidate = (selected: ProductFormData["variants"]) =>
    buildPreOrderCandidate({
      product,
      moduleId,
      variants,
      discounts,
      selectedVariants: selected
    });

  const extraInclVat = previewPickedExtrasInclVat({
    product,
    variants,
    discounts,
    selectedVariants
  });

  const onSubmit = (data: ProductFormData) => {
    const candidate = toCandidate(data.variants);

    if (!takeCandidate({ candidate, productName: product.name })) return;

    navigate(-1);
  };

  return (
    <form
      className="flex h-screen w-full flex-col gap-6 bg-interface-neutral-1 p-6"
      onSubmit={handleSubmit(onSubmit)}
    >
      <VariantHeader
        productName={product.name}
        cta={
          <Button
            className="rounded-full bg-interface-neutral-6"
            type="submit"
            variant="primary"
            size="large"
          >
            <T _str="Continue" />

            {extraInclVat === 0 ? null : (
              <span className="ml-2 font-light">
                {priceFormatter({
                  value: extraInclVat,
                  currency,
                  locale
                })}
              </span>
            )}
          </Button>
        }
      >
        <AllergensIcons allergens={product.allergens} />
      </VariantHeader>

      <VariantsGridLayout>
        {variants.map((variant) => (
          <Controller
            key={variant.id}
            name={`variants.${variant.id}`}
            control={control}
            rules={{
              validate: (choices) => {
                if (!variant.minimum) return true;

                let totalQuantity = 0;
                for (const key in choices) {
                  totalQuantity += choices[key];
                }

                return totalQuantity > 0 ? true : "required";
              }
            }}
            render={({ field }) => (
              <VariantForm
                key={variant.id}
                variant={variant}
                variantFormData={field.value}
                onChange={field.onChange}
                isError={Boolean(errors.variants?.[variant.id])}
                currency={currency}
                locale={locale}
                vatRate={product.taxRate.vatRate}
                matchAllergies={matchAllergiesFor(moduleId)}
              />
            )}
          />
        ))}
      </VariantsGridLayout>
    </form>
  );
};
